/**
 * BACKOFFICE LOGISTIQUE — Helpers transverses d'activité (#662).
 * Opérateur courant, capture géoloc, événements d'activité (statusHistory)
 * et notifications logistiques tracées. Extraits tels quels du god-service
 * `backoffice.service.ts` : ces helpers étaient partagés par les domaines
 * réception / conteneurs / livraison — les sortir dans un service commun
 * évite la duplication (règle « injecte le voisin, ne duplique jamais »).
 */

import { Injectable } from '@angular/core';
import { Firestore, doc, updateDoc, arrayUnion } from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { take } from 'rxjs/operators';
import { GeolocationService } from '../../core/services/geolocation.service';
import { AuthService } from '../../core/services/auth.service';
import { ActivityCategory } from '../models/backoffice.models';
import {
  NOTIFICATION_SENT,
  NotificationOutcome,
  readNotificationOutcome,
} from '../models/notification-outcome';

// #1416 — la dérivation vit dans `models/notification-outcome.ts` depuis #1358
// (un second appelant en avait besoin : la réponse SAV). Ré-exportée ici pour
// les importeurs historiques.
export type { NotificationOutcome } from '../models/notification-outcome';
export { NOTIFICATION_SENT } from '../models/notification-outcome';

@Injectable({ providedIn: 'root' })
export class BackofficeActivityService {

  constructor(
    private firestore: Firestore,
    private functions: Functions,
    private geolocationService: GeolocationService,
    private authService: AuthService
  ) {}

  /**
   * Retourne l'identité de l'opérateur connecté (ou 'Système' si non authentifié).
   */
  getCurrentOperator(): { uid: string; name: string; email: string } {
    const user = this.authService.user;
    if (user) {
      return {
        uid: user.uid,
        name: user.displayName || user.email || 'Agent',
        email: user.email || '',
      };
    }
    return { uid: 'system', name: 'Système', email: '' };
  }

  /**
   * Capture la géolocalisation courante (non-bloquant, timeout 5s).
   * Retourne null si la détection échoue.
   */
  async captureLocation(): Promise<{ city?: string; country?: string; lat?: number; lng?: number } | null> {
    try {
      const result = await this.geolocationService.detectLocation().pipe(take(1)).toPromise();
      if (result?.success && result.data) {
        return {
          city: result.data.city,
          country: result.data.country,
          lat: result.data.latitude,
          lng: result.data.longitude,
        };
      }
      return null;
    } catch {
      return null;
    }
  }

  // T14/C3 Phase 2 (2026-05-20) : recordLocationForMap() removed.
  // Le mirror parcelLocationHistory est désormais dérivé server-side par
  // le trigger syncStatusToLocationMirror depuis shipments.statusHistory
  // (chaque buildActivityEvent ci-dessous ajoute déjà `details.location`
  // au statusHistory, donc le trigger enrichit le mirror automatiquement).
  // Les rules Firestore bloquent les writes client sur parcelLocationHistory.

  /**
   * Crée un événement d'activité enrichi pour le fil d'activité unifié.
   * Chaque appel génère un événement unique (pas de déduplication — l'historique est un journal).
   */
  buildActivityEvent(
    status: string,
    label: string,
    category: ActivityCategory,
    source: string,
    details: Record<string, any> = {},
    automated = false
  ): { statusHistory: ReturnType<typeof arrayUnion> } {
    const id = `${status}_${Date.now()}_${Math.random().toString(36).slice(-4)}`;
    const operator = automated ? { uid: 'system', name: 'Système', email: '' } : this.getCurrentOperator();
    return {
      statusHistory: arrayUnion({
        id,
        category,
        status,
        label,
        timestamp: new Date(),
        source,
        operator,
        details,
        automated,
      })
    };
  }

  /**
   * Ajoute un événement d'activité à un shipment existant (usage externe).
   */
  async addActivityEvent(
    shipmentId: string,
    status: string,
    label: string,
    category: ActivityCategory,
    source: string,
    details: Record<string, any> = {},
    automated = false
  ): Promise<void> {
    const shipmentRef = doc(this.firestore, 'shipments', shipmentId);
    const id = `${status}_${Date.now()}_${Math.random().toString(36).slice(-4)}`;
    const operator = automated ? { uid: 'system', name: 'Système', email: '' } : this.getCurrentOperator();
    await updateDoc(shipmentRef, {
      statusHistory: arrayUnion({
        id, category, status, label,
        timestamp: new Date(),
        source, operator, details, automated,
      }),
      'timestamps.updatedAt': new Date()
    });
  }

  /**
   * #1416 — la trace suit ce que la CF a RÉELLEMENT fait, pas l'absence
   * d'exception.
   *
   * `sendLogisticsNotification` n'échoue pas en levant : un envoi dont tous les
   * canaux ont échoué revient normalement, avec `success: false`. Se contenter
   * du `try/catch` écrivait « Notification envoyée » dans la timeline du colis
   * pour un envoi qui n'était jamais parti — le motif n'étant visible que pour
   * qui allait ouvrir `notification_logs`.
   */
  async sendNotification(payload: any, shipmentId?: string): Promise<void> {
    let outcome = NOTIFICATION_SENT;
    let thrown: any = null;
    try {
      const callable = httpsCallable<any, any>(this.functions, 'sendLogisticsNotification');
      const res = await callable(payload);
      outcome = this.readNotificationOutcome(res?.data);
    } catch (err: any) {
      outcome = { result: 'failed', errorMsg: err?.message || 'Unknown error', failedChannels: [] };
      thrown = err;
    } finally {
      const { result, errorMsg, failedChannels, cfStatus } = outcome;
      // Trace notification in activity timeline (non-blocking)
      if (shipmentId) {
        const status = result === 'sent' ? 'notification_sent' : 'notification_failed';
        const channelLabel = (payload.channels || []).join(' + ') || 'email';
        const failedLabel = failedChannels.length ? failedChannels.join(' + ') : channelLabel;
        const label = result === 'sent'
          ? `Notification envoyee (${channelLabel})`
          : cfStatus === 'partial'
            ? `Notification partielle (echec: ${failedLabel})`
            : `Echec notification (${failedLabel})`;
        this.addActivityEvent(
          shipmentId, status, label, 'notification', 'system_notification',
          {
            notificationType: payload.type,
            recipientEmail: payload.recipientEmail || undefined,
            recipientPhone: payload.recipientPhone || undefined,
            channels: payload.channels || [],
            body: payload.data || {},
            result,
            ...(cfStatus && { deliveryStatus: cfStatus }),
            ...(failedChannels.length > 0 && { failedChannels }),
            ...(errorMsg && { error: errorMsg }),
          },
          true // automated
        ).catch(e => console.warn('⚠️ Failed to trace notification in timeline:', e));
      }
    }

    // Re-throw so callers still see the error — après la trace, comme avant.
    if (thrown) throw thrown;
    if (outcome.result === 'failed') {
      throw new Error(outcome.errorMsg);
    }
  }

  /**
   * #1416 — lit la réponse de `sendLogisticsNotification`.
   *
   * Séparé du reste parce que c'est LA décision du correctif, et que les
   * fonctions modulaires `@angular/fire` (`httpsCallable`) ne sont pas
   * interceptables sous Karma (cf. b2b-dashboard.component.spec, #987) : sans
   * cette découpe, la règle ne serait couverte par aucun test. Depuis #1358 la
   * décision elle-même vit dans `models/notification-outcome.ts` — cette
   * méthode n'en est plus que le point d'entrée pour ce service.
   */
  readNotificationOutcome(data: any): NotificationOutcome {
    return readNotificationOutcome(data);
  }
}
